import { createServerFn } from "@tanstack/react-start";

import type { Capa, PostResumo } from "@/lib/blog.functions";

export type PerguntaArtigo = { pergunta: string; resposta: string };
export type ReferenciaArtigo = { titulo: string; url: string | null };

export type Artigo = {
  slug: string;
  title: string | null;
  h1: string | null;
  subtitle: string | null;
  direct_answer: string | null;
  body: string | null;
  faq: PerguntaArtigo[];
  referencias: ReferenciaArtigo[];
  related_treatment_path: string | null;
  reading_minutes: number | null;
  published_at: string | null;
  reviewed_at: string | null;
  seo_title: string | null;
  meta_description: string | null;
  categoria: { slug: string; label: string } | null;
  capa: Capa | null;
  relacionados: PostResumo[];
  /** Slugs de posts públicos agora e de categorias, para os links internos do corpo. */
  links: { postsPublicos: string[]; categorias: string[] };
};

const SELECT_ARTIGO =
  "id, slug, title, h1, subtitle, direct_answer, body, faq, references, related_treatment_path, reading_minutes, published_at, reviewed_at, seo_title, meta_description, category_id, post_categories:category_id (slug, label), media:cover_media_id (path, alt, width, height)";

const SELECT_RELACIONADO =
  "slug, title, subtitle, direct_answer, reading_minutes, published_at, post_categories:category_id (slug, label), media:cover_media_id (path, alt, width, height)";

type LinhaRelacionado = {
  slug: string;
  title: string | null;
  subtitle: string | null;
  direct_answer: string | null;
  reading_minutes: number | null;
  published_at: string | null;
  post_categories: { slug: string; label: string } | null;
  media: Capa | null;
};

/** Lê uma lista de perguntas do jsonb, ignorando o que não tiver os dois campos. */
function lerFaq(valor: unknown): PerguntaArtigo[] {
  if (!Array.isArray(valor)) return [];
  return valor
    .map((item) => item as Record<string, unknown>)
    .map((item) => ({
      pergunta: String(item?.["pergunta"] ?? item?.["question"] ?? "").trim(),
      resposta: String(item?.["resposta"] ?? item?.["answer"] ?? "").trim(),
    }))
    .filter((item) => item.pergunta !== "" && item.resposta !== "");
}

function lerReferencias(valor: unknown): ReferenciaArtigo[] {
  if (!Array.isArray(valor)) return [];
  return valor
    .map((item) => {
      if (typeof item === "string") return { titulo: item.trim(), url: null };
      const registro = item as Record<string, unknown>;
      return {
        titulo: String(registro?.["titulo"] ?? registro?.["title"] ?? "").trim(),
        url: registro?.["url"] ? String(registro["url"]) : null,
      };
    })
    .filter((item) => item.titulo !== "");
}

/**
 * Artigo publicado, pelo slug, renderizado no servidor.
 *
 * Rascunho e slug inexistente devolvem null, para que a rota responda 404 em
 * vez de mostrar uma página vazia.
 */
export const getArtigo = createServerFn({ method: "GET" })
  .inputValidator((data: { slug: string }) => data)
  .handler(async ({ data }): Promise<Artigo | null> => {
    // 60 s de cache: um post agendado aparece no máximo 1 minuto depois do horário.
    const { comCache } = await import("./cache.server");
    return comCache(`artigo:${data.slug}`, 60_000, () => carregarArtigoDoBanco(data.slug));
  });

async function carregarArtigoDoBanco(slugPedido: string): Promise<Artigo | null> {
  const data = { slug: slugPedido };
  {
    const supabase = await (await import("@/lib/supabase-publico.server")).clientePublico();

    const agora = new Date().toISOString();
    // Artigo, slugs públicos e categorias saem juntos, numa única ida ao banco.
    const [{ data: linha }, { data: publicos }, { data: categorias }] = await Promise.all([
      supabase
        .from("posts")
        .select(SELECT_ARTIGO)
        .eq("slug", data.slug)
        .eq("status", "publicado").lte("published_at", agora)
        .maybeSingle(),
      supabase.from("posts").select("slug").eq("status", "publicado").lte("published_at", agora),
      supabase.from("post_categories").select("slug"),
    ]);

    if (!linha) return null;
    const post = linha as unknown as Record<string, unknown> & {
      post_categories: { slug: string; label: string } | null;
      media: Capa | null;
    };

    let relacionados: PostResumo[] = [];
    if (post["category_id"]) {
      const { data: linhas } = await supabase
        .from("posts")
        .select(SELECT_RELACIONADO)
        .eq("status", "publicado").lte("published_at", new Date().toISOString())
        .eq("category_id", post["category_id"] as string)
        .neq("slug", data.slug)
        .order("published_at", { ascending: false, nullsFirst: false })
        .limit(3);

      relacionados = ((linhas ?? []) as unknown as LinhaRelacionado[]).map((item) => ({
        slug: item.slug,
        title: item.title,
        subtitle: item.subtitle,
        direct_answer: item.direct_answer,
        reading_minutes: item.reading_minutes,
        published_at: item.published_at,
        categoria: item.post_categories ?? null,
        capa: item.media ?? null,
      }));
    }

    return {
      links: {
        postsPublicos: (publicos ?? []).map((p) => String(p.slug)),
        categorias: (categorias ?? []).map((c) => String(c.slug)),
      },
      slug: String(post["slug"]),
      title: (post["title"] as string | null) ?? null,
      h1: (post["h1"] as string | null) ?? null,
      subtitle: (post["subtitle"] as string | null) ?? null,
      direct_answer: (post["direct_answer"] as string | null) ?? null,
      body: (post["body"] as string | null) ?? null,
      faq: lerFaq(post["faq"]),
      referencias: lerReferencias(post["references"]),
      related_treatment_path: (post["related_treatment_path"] as string | null) ?? null,
      reading_minutes: (post["reading_minutes"] as number | null) ?? null,
      published_at: (post["published_at"] as string | null) ?? null,
      reviewed_at: (post["reviewed_at"] as string | null) ?? null,
      seo_title: (post["seo_title"] as string | null) ?? null,
      meta_description: (post["meta_description"] as string | null) ?? null,
      categoria: post.post_categories ?? null,
      capa: post.media ?? null,
      relacionados,
    };
  }
}
